/**
 * [Gurumi 블록 메뉴]
 * 역할: 가가 메뉴 — 제목~모노·본문은 선택 구간 인라인, 목록/인용은 블록
 * 작성/수정일: 2026-07-29
 */
export const EDITOR_BLOCK_OPTIONS = [
  { type: 'title', label: '제목', previewClass: 'block-preview-title' },
  { type: 'heading', label: '머리말', previewClass: 'block-preview-heading' },
  { type: 'subheading', label: '부머리말', previewClass: 'block-preview-subheading' },
  { type: 'paragraph', label: '본문', previewClass: 'block-preview-body' },
  { type: 'mono', label: '모노 스타일', previewClass: 'block-preview-mono' },
  { type: 'bullet', label: '구분점 목록', marker: '•', previewClass: 'block-preview-body' },
  { type: 'dash', label: '대시 목록', marker: '-', previewClass: 'block-preview-body' },
  { type: 'ordered', label: '번호 목록', marker: '1.', previewClass: 'block-preview-body' },
  { type: 'blockquote', label: '블록 인용', marker: '|', previewClass: 'block-preview-body', dividerBefore: true },
];

/** [가가 텍스트 스타일] 역할: 인라인 fontVariant 를 쓰는 메뉴 타입 — 작성/수정일: 2026-07-29 */
const INLINE_FONT_TYPES = {
  title: 'title',
  heading: 'heading',
  subheading: 'subheading',
  paragraph: 'body',
  mono: 'mono',
};

/**
 * [가가 텍스트 스타일]
 * 역할: 드래그 선택을 유지한 채 커맨드 실행
 * 작성/수정일: 2026-07-29
 */
function runWithSelection(editor, run) {
  const { from, to } = editor.state.selection;
  const chain = editor.chain().focus().setTextSelection({ from, to });
  return run(chain);
}

/**
 * [가가 텍스트 스타일]
 * 역할: 인라인 크기 스타일은 비어 있지 않은 선택에서만 적용
 * 작성/수정일: 2026-07-29
 */
function requireTextSelection(editor) {
  const { empty, from, to } = editor.state.selection;
  if (empty || from === to) {
    window.alert('글자 크기를 바꿀 텍스트를 드래그해서 선택해 주세요.');
    return false;
  }
  return true;
}

export function getActiveEditorBlockType(editor) {
  if (editor.isActive('fontVariant', { variant: 'title' })) return 'title';
  if (editor.isActive('fontVariant', { variant: 'heading' })) return 'heading';
  if (editor.isActive('fontVariant', { variant: 'subheading' })) return 'subheading';
  if (editor.isActive('fontVariant', { variant: 'mono' })) return 'mono';
  if (editor.isActive('fontVariant', { variant: 'body' })) return 'paragraph';

  if (editor.isActive('heading', { level: 1 })) return 'title';
  if (editor.isActive('heading', { level: 2 })) return 'heading';
  if (editor.isActive('heading', { level: 3 })) return 'subheading';
  if (editor.isActive('codeBlock')) return 'mono';
  if (editor.isActive('bulletList', { listStyle: 'dash' })) return 'dash';
  if (editor.isActive('bulletList')) return 'bullet';
  if (editor.isActive('orderedList')) return 'ordered';
  if (editor.isActive('blockquote')) return 'blockquote';
  return 'paragraph';
}

/**
 * [가가 텍스트 스타일]
 * 역할: 제목/머리말/부머리말/본문/모노 → 선택 구간만 인라인 적용
 * 작성/수정일: 2026-07-29
 */
function applyInlineFontType(editor, menuType) {
  if (!requireTextSelection(editor)) return false;

  const variant = INLINE_FONT_TYPES[menuType];
  if (!variant) return false;

  const already = editor.isActive('fontVariant', { variant });
  if (already) {
    return runWithSelection(editor, (chain) => chain.unsetFontVariant().run());
  }

  return runWithSelection(editor, (chain) => chain.setFontVariant(variant).run());
}

export function applyEditorBlockType(editor, type) {
  if (Object.prototype.hasOwnProperty.call(INLINE_FONT_TYPES, type)) {
    return applyInlineFontType(editor, type);
  }

  const current = getActiveEditorBlockType(editor);
  if (current === type) {
    return runWithSelection(editor, (chain) => chain.clearNodes().setParagraph().run());
  }

  switch (type) {
    case 'bullet':
      if (editor.isActive('bulletList', { listStyle: 'dash' })) {
        return runWithSelection(editor, (c) => c.updateAttributes('bulletList', { listStyle: 'bullet' }).run());
      }
      if (editor.isActive('bulletList')) return runWithSelection(editor, (c) => c.toggleBulletList().run());
      if (editor.isActive('orderedList')) {
        return runWithSelection(editor, (c) =>
          c.toggleOrderedList().toggleBulletList().updateAttributes('bulletList', { listStyle: 'bullet' }).run(),
        );
      }
      return runWithSelection(editor, (c) =>
        c.toggleBulletList().updateAttributes('bulletList', { listStyle: 'bullet' }).run(),
      );
    case 'dash':
      if (editor.isActive('bulletList', { listStyle: 'dash' })) {
        return runWithSelection(editor, (c) => c.toggleBulletList().run());
      }
      if (editor.isActive('bulletList')) {
        return runWithSelection(editor, (c) => c.updateAttributes('bulletList', { listStyle: 'dash' }).run());
      }
      return runWithSelection(editor, (c) =>
        c.toggleBulletList().updateAttributes('bulletList', { listStyle: 'dash' }).run(),
      );
    case 'ordered':
      if (editor.isActive('orderedList')) return runWithSelection(editor, (c) => c.toggleOrderedList().run());
      if (editor.isActive('bulletList')) {
        return runWithSelection(editor, (c) => c.toggleBulletList().toggleOrderedList().run());
      }
      return runWithSelection(editor, (c) => c.toggleOrderedList().run());
    case 'blockquote':
      return runWithSelection(editor, (c) => c.clearNodes().toggleBlockquote().run());
    default:
      return false;
  }
}
